


























































































































































































import {Component, Mixins, Prop, Ref, Watch} from 'vue-property-decorator';
import {colord, Colord} from "colord"
import MonitorMixin from "@/design-system/utilities/MonitorMixin"
import JdhBoundedSelector from "@/design-system/shared/JdhBoundedSelector.vue"
import JdhMicroInput from "@/design-system/shared/JdhMicroInput.vue"
import JdhSizeDetector from "@/design-system/shared/JdhSizeDetector.vue"

// we try to perform comparisons in the HSL space, to avoid problems with precision with, say,
// hex level comparisons. This function is no longer needed after the `lastEmit` was added,
// but it may be useful again in this context.
//
// function colorsDiffer(c1: Colord, c2: Colord) {
//     if ([0, 100].includes(c1.toHsl().l) || [0, 100].includes(c2.toHsl().l)) {
//         // if colors are pure white or black, then all that matters is that they differ
//         return c1.toHsl().l !== c2.toHsl().l
//     } else if (c1.toHsl().s === 0 && c2.toHsl().s === 0) {
//         // if colors are greyscale, we ignore the hue aspect when comparing
//         return c1.toHsl().s !== c2.toHsl().s || c1.toHsl().l !== c2.toHsl().l
//     }else {
//         // console.log('c', c1.toHslString(), c2.toHslString())
//         return c1.toHslString() !== c2.toHslString()
//     }
// }

// get coords from [0~1], [0~1] and convert to [0~359] deg heading. Used for the ring inputs.
function coaxBoxToPolarAngle(x: number, y: number): number {
    const degrees = Math.atan2(x, y) * 180 / Math.PI
    return Math.floor(180 + (degrees * -1))
}

/**
 * @status stable
 * @published $next
 * @changelog $next Published.
 */
@Component({
    components: {JdhSizeDetector, JdhMicroInput, JdhBoundedSelector}
})
export default class JdhLogicalColorPicker extends Mixins(MonitorMixin) {
    @Prop() value!: string

    // Unlike most vue-convention inputs, this one holds internal state. This is because
    // the color picker can have interim UI states that are not reflected by the color output,
    // due to color space and mathematical restrictions.
    innerState: Colord = colord('#fff')

    // hold an internal hue state, because hue is undefined when sat = 0. Be sure to update this
    // whenever hues change deliberately!
    innerHue = 0

    // hold internal state for the data panel
    innerDataHex = ''
    innerDataH = 0
    innerDataS = 0
    innerDataL = 100
    innerDataR = 255
    innerDataG = 255
    innerDataB = 255

    // pre-warning: the names here are horrible unscientific. I didn't study astronomy for 2
    // years in uni just to wind up using all that math to make a color picker. Sorry. -- Rizqi

    // state for the rectangle picker, all [0~1]
    shadeBoxSelectX = 0 // saturation axis
    shadeBoxSelectY = 0 // value axis
    hueBoxSelectX = 0 // hue axis

    // state for the triangle picker
    triangleHueSelect = 0 // hue axis, [0~360]
    triangleToneX = 0 // [0~1]
    triangleToneY = 0 // [0~1]

    // state for sphere picker
    sphereLightnessSelect = 180
    sphereSelectAngle = 0
    sphereSelectDistance = 0
    sphereInnerSaturation = 0 // similar to innerHue, only used for the sphere

    // width of rings, when used, as a percentage of available space.
    ringWidth = 5

    presets = [
        ['#e53e3e', '#dd6b20', '#d69e2e', '#38a169'],
        ['#319795', '#3182ce', '#805ad5', '#d53f8c'],
    ]

    // keep track of the most recent emit, to avoid infinite loop updates.
    lastEmit = ''

    // hold this just for auto-focus.
    @Ref() hexEl!: HTMLElement

    // toggle elements based on available width.
    showExtra = true
    showBox = true

    mounted() {
        if (navigator.maxTouchPoints == 0) this.hexEl.focus()
    }

    changeState(target: Colord) {
        this.innerState = target
        this.$emit('input', this.innerState.toHex())
        this.lastEmit = this.innerState.toHex()
        this.$debug('emitting new color state')
    }

    @Watch('value', {immediate: true})
    syncInnerState(value: string) {
        // bail early if it matches the last thing we emitted.
        if (this.lastEmit === colord(value).toHex()) return

        // if the inbound color is different from the most recent state, that means the value
        // changed externally, and we should tell the pickers to update.
        // if (!this.innerState || colorsDiffer(colord(value), this.innerState)) {
        this.innerState = colord(value)
        this.innerHue = this.innerState.toHsl().h
        this.issueUpdates()
        this.$debug('updating inner color state')
        // }
    }

    issueUpdates(exclude?: 'data-hsl' | 'data-rgb' | 'data-hex' | 'rectangle' | 'triangle' | 'sphere') {
        if (exclude !== 'rectangle') this.changeRectColor(this.innerState)
        if (exclude !== 'triangle') this.changeTriangleColor(this.innerState)
        if (exclude !== 'sphere') this.changeSphereColor(this.innerState)
        if (exclude !== 'data-hsl') this.changeDataColorHsl(this.innerState)
        if (exclude !== 'data-rgb') this.changeDataColorRgb(this.innerState)
        if (exclude !== 'data-hex') this.changeDataColorHex(this.innerState)
    }

    /**
     * General-purpose "focus me" handler.
     */
    select(e: FocusEvent) {
        if (navigator.maxTouchPoints > 0) return
        (e.target as HTMLInputElement).select()
    }

    handleResize(size: DOMRectReadOnly) {
        this.showExtra = size.width > 600
        this.showBox = size.width > 248
    }

    /**
     * ------------ RAW DATA ------------
     *
     * For the people who want to work directly with color params.
     */

    get currentColorStyles() {
        return {
            backgroundColor: this.innerState.toHex(),
            color: this.innerState.isDark() ? 'white' : 'black',
        }
    }

    changeDataHue(e: KeyboardEvent) {
        const h = Math.min(Math.max(Number.parseInt((e.target as HTMLInputElement).value) || 0, 0), 360)
        this.innerHue = h
        this.innerDataH = h
        this.changeState(colord({
            h,
            s: this.innerState.toHsl().s,
            l: this.innerState.toHsl().l,
        }))
        this.issueUpdates('data-hsl')
    }

    changeDataSat(e: KeyboardEvent) {
        const s = Math.min(Math.max(Number.parseInt((e.target as HTMLInputElement).value) || 0, 0), 100)
        this.innerDataS = s
        this.changeState(colord({
            h: this.innerHue,
            s,
            l: this.innerState.toHsl().l,
        }))
        this.issueUpdates('data-hsl')
    }

    changeDataLightness(e: KeyboardEvent) {
        const l = Math.min(Math.max(Number.parseInt((e.target as HTMLInputElement).value) || 0, 0), 100)
        this.innerDataL = l
        this.changeState(colord({
            h: this.innerHue,
            s: this.innerState.toHsl().s,
            l,
        }))
        this.issueUpdates('data-hsl')
    }

    changeDataR(e: KeyboardEvent) {
        const val = Math.min(Math.max(Number.parseInt((e.target as HTMLInputElement).value) || 0, 0), 255)
        this.innerDataR = val
        this.changeState(colord({
            r: val,
            g: this.innerState.toRgb().g,
            b: this.innerState.toRgb().b,
        }))
        this.innerHue = this.innerState.hue()
        this.issueUpdates('data-rgb')
    }

    changeDataG(e: KeyboardEvent) {
        const val = Math.min(Math.max(Number.parseInt((e.target as HTMLInputElement).value) || 0, 0), 255)
        this.innerDataG = val
        this.changeState(colord({
            r: this.innerState.toRgb().r,
            g: val,
            b: this.innerState.toRgb().b,
        }))
        this.innerHue = this.innerState.hue()
        this.issueUpdates('data-rgb')
    }

    changeDataB(e: KeyboardEvent) {
        const val = Math.min(Math.max(Number.parseInt((e.target as HTMLInputElement).value) || 0, 0), 255)
        this.innerDataB = val
        this.changeState(colord({
            r: this.innerState.toRgb().r,
            g: this.innerState.toRgb().g,
            b: val,
        }))
        this.innerHue = this.innerState.hue()
        this.issueUpdates('data-rgb')
    }

    changeDataHex(e: KeyboardEvent) {
        let val = (e.target as HTMLInputElement).value
        if (val.substr(0, 1) !== '#') {
            val = '#' + val
        }
        if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(val)) {
            this.innerDataHex = val
            this.changeState(colord(val))
            this.innerHue = this.innerState.hue()
            this.issueUpdates('data-hex')
        }
    }

    checkDataHex(e: KeyboardEvent) {
        const val = (e.target as HTMLInputElement).value
        if (colord(val).toHex() !== this.innerState.toHex()) {
            this.innerDataHex = this.innerState.toHex()
            this.$forceUpdate()
        }
    }

    changeDataColorHsl(color: Colord) {
        this.innerDataH = Math.round(this.innerHue)
        this.innerDataS = Math.round(color.toHsl().s)
        this.innerDataL = Math.round(color.toHsl().l)
    }

    changeDataColorRgb(color: Colord) {
        this.innerDataR = Math.round(color.toRgb().r)
        this.innerDataG = Math.round(color.toRgb().g)
        this.innerDataB = Math.round(color.toRgb().b)
    }

    changeDataColorHex(color: Colord) {
        this.innerDataHex = color.toHex()
    }

    pickPreset(target: string) {
        const color = colord(target)
        this.changeState(color)
        this.innerHue = color.hue()
        this.issueUpdates()
    }

    /**
     * ------------ RECTANGLE ------------
     *
     * The rectangle color picker is a HSV box, where the X-axis represents saturation and the
     * Y-axis represents value.
     *
     * Remember that value is max(R, G, B). This means a pure color is 100% value, but in the HSL
     * system, only 50% lightness.
     */

    get shadeBoxSelectorStyles() {
        return {
            left: `${this.shadeBoxSelectX * 100}%`,
            top: `${this.shadeBoxSelectY * 100}%`,
            background: this.innerState.toHex()
        }
    }

    get hueBoxSelectorStyles() {
        return {
            left: `${this.hueBoxSelectX * 100}%`,
            top: '8px',
            background: colord({
                h: this.innerHue,
                s: 50,
                l: 50
            }).toHex()
        }
    }

    selectInShadeBox({x, y}: { x: number, y: number }) {
        this.shadeBoxSelectX = x
        this.shadeBoxSelectY = y
        this.changeState(colord({h: this.innerHue, s: 100 * x, v: 100 - (100 * y)}))
        this.issueUpdates('rectangle')

    }

    selectInHueBox({x}: { x: number }) {
        this.hueBoxSelectX = x
        this.innerHue = 360 * x
        this.changeState(colord({
            h: this.innerHue,
            s: this.innerState?.toHsv().s,
            v: this.innerState?.toHsv().v
        }))
        this.issueUpdates('rectangle')
    }

    changeRectColor(color: Colord) {
        this.shadeBoxSelectX = color.toHsv().s / 100
        this.shadeBoxSelectY = 1 - (color.toHsv().v / 100)
        this.hueBoxSelectX = this.innerHue / 360
    }

    get shadeBoxStyles() {
        const target = colord({h: this.innerHue, s: 100, v: 100}).toHex()
        return {
            background: `linear-gradient(90deg, white, ${target})`
        }
    }

    /**
     * ------------ TRIANGLE ------------
     *
     * This composed of a hue ring, where degrees on the hue circle correspond to degrees on the circle,
     * and a tone triangle, which has one edge of maximum value, one edge of maximum saturation, and
     * one edge of zero saturation. It can be easier to think of it as an ice-cream-cone representation
     * of the rectangle's shade box, where the bottom of the box is squished into one point.
     */

    get triangleHueSelectorStyles() {
        const pos = (100 - this.ringWidth) / 2
        return {
            left: `${50 + (Math.cos((this.triangleHueSelect - 90) * Math.PI / 180) * pos)}%`,
            top: `${50 + (Math.sin((this.triangleHueSelect - 90) * Math.PI / 180) * pos)}%`,
            background: colord({
                h: this.innerHue,
                s: 50,
                l: 50
            }).toHex()
        }
    }

    get triangleToneSelectorStyles() {
        return {
            left: `${this.triangleToneX * 100}%`,
            top: `${this.triangleToneY * 100}%`,
            background: this.innerState.toHex()
        }
    }

    selectInTriangleHues({x, y}: { x: number, y: number }) {
        // go from [0~1] to [-1~1].
        let nx = 2 * (x - 0.5)
        let ny = 2 * (y - 0.5)
        // from offsets to the hue degrees
        this.triangleHueSelect = coaxBoxToPolarAngle(nx, ny)

        this.innerHue = this.triangleHueSelect
        this.changeState(colord({
            h: this.innerHue,
            s: this.innerState?.toHsv().s,
            v: this.innerState?.toHsv().v
        }))

        this.issueUpdates('triangle')
    }

    selectInTriangleTones({x, y}: { x: number, y: number }) {
        // constant to convert from x to y absolute coords from percentages
        const k = 64 / 74
        const tan60 = 1.732
        const tan30 = 0.577

        // start by constraining the x,y inside the triangle.
        // ... this is really weird, see triangle-zones.png for an explanation why
        // any of this should work
        let mode: null | 'mid' | 'left-edge' | 'top-edge' | 'bottom-edge' | 'top-pin' | 'bottom-pin' | 'right-pin' = null
        if ((y > 0 && y <= 0.5 && x >= 0 && x < 1 && k * x < tan60 * y)
            || (y >= 0.5 && y < 1 && x >= 0 && x < 1 && k * x < (1 - y) * tan60)) {
            mode = 'mid'
        } else if (y > 0 && y < 1 && x <= 0) {
            mode = 'left-edge'
        } else if ((y < 0 && x < 0) || (y < 0 && (k * x < Math.abs(y) * tan30))) {
            mode = 'top-pin'
        } else if ((y > 1 && x < 0) || (y > 1 && (k * x < (y - 1) * tan30))) {
            mode = 'bottom-pin'
        } else if ((x > 1 && y < 0.5 && k * (x - 1) > (0.5 - y) * tan30)
            || (x > 1 && y > 0.5 && k * (x - 1) > (y - 0.5) * tan30)) {
            mode = 'right-pin'
        } else if (y < 0.5) {
            mode = 'top-edge'
        } else {
            mode = 'bottom-edge'
        }

        // now actually do the constraining
        switch (mode) {
            case 'mid': {
                this.triangleToneX = x
                this.triangleToneY = y
                break
            }
            case 'top-pin': {
                this.triangleToneX = 0
                this.triangleToneY = 0
                break
            }
            case 'right-pin': {
                this.triangleToneX = 1
                this.triangleToneY = 0.5
                break
            }
            case 'bottom-pin': {
                this.triangleToneX = 0
                this.triangleToneY = 1
                break
            }
            case 'top-edge': {
                // step up until we're back inside
                let ax = k * x
                let ay = y

                let safety = 0
                while (ax > tan60 * ay && safety < 1000) {
                    ax -= 0.01 * tan30
                    ay += 0.01
                    safety++
                }

                // snap to border
                ax = tan60 * ay

                // renormalize and store
                this.triangleToneX = ax * (1 / k)
                this.triangleToneY = ay
                break
            }
            case 'bottom-edge': {
                // step up until we're back inside
                let ax = k * x
                let ay = y

                let safety = 0
                while (ax > (1 - ay) * tan60 && safety < 1000) {
                    ax -= 0.01 * tan30
                    ay -= 0.01
                    safety++
                }

                // snap to border
                ax = tan60 * (1 - ay)

                // renormalize and store
                this.triangleToneX = ax * (1 / k)
                this.triangleToneY = ay
                break
            }
            case 'left-edge': {
                this.triangleToneX = 0
                this.triangleToneY = y
                break
            }
        }

        // now that we're constrained, figure out the HSV from the coords

        // start by rotating everything 30 deg CCW (and using standard cartesian coords, with
        // y going up as you go upwards, rather than the inverted DOM coords).

        const convX = this.triangleToneX * k
        const convY = -this.triangleToneY
        const rotate = Math.PI / 6
        const newX = ((convX * Math.cos(rotate)) - ((convY + 1) * Math.sin(rotate)))
        const newY = (((convX + 1) * Math.sin(rotate)) + ((convY + 1) * Math.cos(rotate)) - 1)

        // bottom: 0, -0.5
        // left corner: -0.5, 0.36
        // right corner: 0.5, 0.36

        // we're now seeing an inverted triangle, where the X-axis is sat, and Y-axis is value.
        // these are easier to calculate, but first, the corners of the triangles are at weird
        // spots, so let's normalize:

        const normY = newY + 0.5
        const fullY = 0.86

        // this is the width of the X-axis at this Y-height.
        const segmentWidth = normY / fullY

        // saturation is based on the X-axis; short-circuit to 0 saturation if we're at the bottom,
        // and snap to full saturation if we're at the edge
        const newS =
            Math.abs(normY - fullY) < 0.001 ? 0 :
                mode === 'bottom-edge' ? 1 : (newX + (segmentWidth / 2)) / segmentWidth
        // value is based on the Y-axis
        const newV = normY / fullY

        this.changeState(colord({h: this.innerHue, s: 100 * newS, v: 100 * newV}))
        this.issueUpdates('triangle')
    }


    changeTriangleColor(color: Colord) {
        this.triangleHueSelect = this.innerHue

        // this is how much we need to go "down and right" to compensate for the saturation value.
        // I am in awe at how simple this is compared to its opposite operation.
        let targetShift = (color.toHsv().s / 100) * (color.toHsv().v / 100)
        this.triangleToneX = (Math.sin(Math.PI / 3) * targetShift) * (74 / 64)
        this.triangleToneY = (1 - color.toHsv().v / 100) + (Math.cos(Math.PI / 3) * targetShift)
    }

    get triangleToneStyles() {
        const target = colord({h: this.innerHue, s: 100, v: 100}).toHex()
        return {
            background: `conic-gradient(at bottom left, white, ${target} 60deg), linear-gradient(30deg, black, rgba(255,255,255,0) 66%)`
        }
    }


    /**
     * ------------ SPHERE ------------
     *
     * I can only hope this one is less stressful than the triangle. It's just polar coordinates... Right?
     */

    get sphereHueSelectorStyles() {
        const pos = (100 - this.ringWidth) / 2
        return {
            left: `${50 + (Math.cos((this.sphereLightnessSelect - 90) * Math.PI / 180) * pos)}%`,
            top: `${50 + (Math.sin((this.sphereLightnessSelect - 90) * Math.PI / 180) * pos)}%`,
            background: this.innerState.toHex()
        }
    }


    get sphereBaseSelectorStyles() {
        return {
            left: `${50 + (Math.cos((this.sphereSelectAngle - 90) * Math.PI / 180) * this.sphereSelectDistance * 50)}%`,
            top: `${50 + (Math.sin((this.sphereSelectAngle - 90) * Math.PI / 180) * this.sphereSelectDistance * 50)}%`,
            background: this.innerState.toHex()
        }
    }

    selectInSphereLightness({x, y}: { x: number, y: number }) {
        // go from [0~1] to [-1~1].
        let nx = 2 * (x - 0.5)
        let ny = 2 * (y - 0.5)
        // from offsets to the hue degrees
        this.sphereLightnessSelect = coaxBoxToPolarAngle(nx, ny)

        // convert the two halves of this ring to lightness
        let lightness = 0
        if (this.sphereLightnessSelect > 180) {
            lightness = (360 - this.sphereLightnessSelect) / 180
        } else {
            lightness = this.sphereLightnessSelect / 180
        }

        this.changeState(colord({
            h: this.innerHue,
            s: this.sphereInnerSaturation,
            l: lightness * 100
        }))

        this.issueUpdates('sphere')
    }

    selectInSphereBase({x, y}: { x: number, y: number }) {
        // go from [0~1] to [-1~1].
        let nx = 2 * (x - 0.5)
        let ny = 2 * (y - 0.5)
        // from offsets to the hue degrees
        this.sphereSelectAngle = coaxBoxToPolarAngle(nx, ny)
        this.sphereSelectDistance = Math.min(Math.sqrt(Math.pow(nx, 2) + Math.pow(ny, 2)), 1)

        this.innerHue = this.sphereSelectAngle
        this.sphereInnerSaturation = this.sphereSelectDistance * 100
        this.changeState(colord({
            h: this.innerHue,
            s: this.sphereSelectDistance * 100,
            l: this.innerState?.toHsl().l
        }))

        this.issueUpdates('sphere')
    }

    changeSphereColor(color: Colord) {
        this.sphereInnerSaturation = this.innerState?.toHsv().s

        // move the lightness selection, but keep it left/right based on where it is now
        if (this.sphereLightnessSelect > 180) {
            this.sphereLightnessSelect = 360 - (color.toHsl().l * 1.8)
        } else {
            this.sphereLightnessSelect = color.toHsl().l * 1.8
        }

        this.sphereSelectAngle = color.toHsl().h
        this.sphereSelectDistance = color.toHsl().s / 100
    }


    get sphereLightnessStyles() {
        const target = colord({h: this.innerHue, s: 100, v: 100}).toHex()
        return {
            background: `linear-gradient(rgba(0,0,0,.6), transparent, rgba(255,255,255,.93)), linear-gradient(${target}, ${target})`
        }
    }

}
